import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, useTooltip, ViewTitle } from '../ui'

const W = 760
const H = 300
const ML = 46
const MR = 18
const MT = 26
const MB = 48
/* Wide enough that neighbouring bars never touch: the gap is what separates
   one band from the next, since they all carry the same colour. */
const GAP = 14

/* Axis tops that divide in half cleanly, so the midpoint tick is a whole
   number of people rather than 3.5 of one. */
const TOPS = [2, 4, 6, 8, 10, 20, 40, 60, 80, 100, 200, 400, 600, 800, 1000]

type RevenuePoint = {
  period: string
  usdCents: number
  people: number
  optometristUsdCents: number
  directUsdCents: number
  optometristPeople: number
  directPeople: number
}

/** Dollars, or the number of people who paid. The same bars, a different unit. */
type Measure = 'money' | 'people'

/* Two ways in, so the split can be read either way round. Optometrist sits on
   the baseline because that is the channel being watched month to month, and a
   segment anchored to the axis is the one you can compare across columns. */
const SERIES = [
  { key: 'optometrist', label: 'Through optometrists', fill: 'var(--s2)' },
  { key: 'direct', label: 'Direct', fill: 'var(--s1)' },
] as const

const usd = (cents: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(cents / 100)

/**
 * A rectangle with only one end rounded, so a stack reads as one bar.
 *
 * Rounding every corner of both segments would draw two lozenges with a pinch
 * between them; rounding none would lose the soft data-end the rest of the
 * console has. So the segment touching the axis is square on top, the one on
 * top of the stack is square on the bottom, and a lone segment is rounded at
 * the top like an unsplit bar.
 */
function barPath(x: number, y: number, w: number, h: number, roundTop: boolean): string {
  const r = Math.min(4, w / 2, h)
  if (!roundTop || h <= 0) return `M${x} ${y}h${w}v${h}h${-w}Z`
  return `M${x} ${y + h}v${-(h - r)}a${r} ${r} 0 0 1 ${r} ${-r}h${w - r * 2}a${r} ${r} 0 0 1 ${r} ${r}v${h - r}Z`
}

/**
 * Revenue or payers over time, split by how the sale was made.
 *
 * Stacked rather than side by side: the total is the figure in the panel
 * heading, and a stack keeps the column equal to it. The two segments are drawn
 * with a 2px gap in the surface colour so they stay separable where one of them
 * is only a few pixels tall.
 */
function RevenueChart({
  points,
  label,
  measure,
  bind,
  onOpen,
}: {
  points: RevenuePoint[]
  label: (period: string) => string
  measure: Measure
  bind: (content: React.ReactNode) => Record<string, unknown>
  onOpen: (period: string) => void
}) {
  const W = 760
  const H = 260
  const ML = 62
  const MR = 18
  const MT = 30
  const MB = 44
  const plotW = W - ML - MR
  const plotH = H - MT - MB
  const slot = plotW / Math.max(1, points.length)
  const barW = Math.max(6, slot - Math.min(18, slot * 0.35))

  const money = measure === 'money'
  const total = (p: RevenuePoint) => (money ? p.usdCents : p.people)
  const parts = (p: RevenuePoint) =>
    money
      ? { optometrist: p.optometristUsdCents, direct: p.directUsdCents }
      : { optometrist: p.optometristPeople, direct: p.directPeople }
  const fmt = (v: number) => (money ? usd(v) : v.toLocaleString())
  /* Thirty days of bars leave no room for "$15,984" over each: the amount goes
     short ("$16k") and every other day is named. The tooltip keeps the exact sum. */
  const dense = points.length > 16
  const short = (v: number) =>
    !money || v < 100_000 ? fmt(v) : `$${(v / 100_000).toFixed(v < 1_000_000 ? 1 : 0).replace(/\.0$/, '')}k`

  const peak = Math.max(1, ...points.map(total))
  /* A round number above the tallest bar, so the axis reads in thousands rather
     than in whatever the largest month happened to be. In people, a round
     number is also a whole one — nobody is 3.5 patients. */
  const step = money
    ? Math.pow(10, Math.floor(Math.log10(peak)))
    : Math.max(2, Math.pow(10, Math.floor(Math.log10(peak))))
  const top = Math.ceil(peak / step) * step
  const y = (v: number) => MT + plotH * (1 - v / top)
  const cx = (i: number) => ML + slot * i + slot / 2

  return (
    <div className="chart-wrap">
      {/* Two series, so a legend is always present: the colours alone would
          leave the split unreadable in print or to a colourblind reader. */}
      <div className="legend">
        {SERIES.map((sx) => (
          <span key={sx.key} className="legend-item">
            <span className="swatch" style={{ background: sx.fill }} aria-hidden="true" />
            {sx.label}
          </span>
        ))}
      </div>
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={points.map((p) => `${label(p.period)}: ${fmt(total(p))}`).join('. ')}
      >
        {[0, top / 2, top].map((t) => (
          <g key={t}>
            <line
              x1={ML}
              x2={W - MR}
              y1={y(t)}
              y2={y(t)}
              stroke={t === 0 ? 'var(--rule)' : 'var(--grid)'}
              strokeWidth={1}
            />
            <text x={ML - 10} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--ink-3)">
              {fmt(t)}
            </text>
          </g>
        ))}
        {points.map((p, i) => {
          const v = total(p)
          const part = parts(p)
          /* Stacked from the axis up. Each segment keeps its own height so a
             month that is one $600 sale through a practice still shows the
             orange, rather than rounding away under the blue. */
          let cursor = y(0)
          const segments = SERIES.map((sx) => {
            const value = part[sx.key]
            const h = value === 0 ? 0 : Math.max(2, y(0) - y(value))
            const yTop = cursor - h
            cursor = yTop
            return { ...sx, value, h, y: yTop }
          }).filter((sg) => sg.value > 0)
          const topMost = segments[segments.length - 1]

          return (
            <g
              key={p.period}
              className={v > 0 ? 'bar-hit' : undefined}
              {...(v > 0
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => onOpen(p.period),
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onOpen(p.period)
                      }
                    },
                    'aria-label': `${label(p.period)}: ${fmt(v)} — open these patients`,
                  }
                : {})}
              {...bind(
                <>
                  <b>{label(p.period)}</b>
                  <div className="r">
                    <span>Through optometrists</span>
                    <span>{fmt(part.optometrist)}</span>
                  </div>
                  <div className="r">
                    <span>Direct</span>
                    <span>{fmt(part.direct)}</span>
                  </div>
                  <div className="r">
                    <span>{money ? 'Total' : 'Patients'}</span>
                    <span>{fmt(v)}</span>
                  </div>
                  {money && (
                    <div className="r">
                      <span>Payments</span>
                      <span>{p.people}</span>
                    </div>
                  )}
                  {v > 0 && <div className="hint">Click to see who</div>}
                </>,
              )}
            >
              <rect
                className="hit"
                x={ML + slot * i}
                y={MT}
                width={slot}
                height={plotH}
                fill="transparent"
              />
              {segments.map((sg) => (
                <g key={sg.key}>
                  <path
                    d={barPath(cx(i) - barW / 2, sg.y, barW, sg.h, sg === topMost)}
                    fill={sg.fill}
                  />
                  {/* A 2px gap in the surface colour between the two, rather
                      than a stroke: a stroke would eat into a short segment. */}
                  {sg !== topMost && (
                    <rect
                      x={cx(i) - barW / 2}
                      y={sg.y - 2}
                      width={barW}
                      height={2}
                      fill="var(--surface)"
                    />
                  )}
                </g>
              ))}
              {v > 0 && (
                /* The amount on the bar, so the chart can be read without
                   tracing each column back to the axis. */
                <text
                  x={cx(i)}
                  y={y(v) - 10}
                  textAnchor="middle"
                  fontSize={dense ? 9.5 : 11.5}
                  fontWeight={600}
                  fill="var(--ink-2)"
                >
                  {dense ? short(v) : fmt(v)}
                </text>
              )}
              {(!dense || (points.length - 1 - i) % 2 === 0) && (
                <text
                  x={cx(i)}
                  y={H - MB + 20}
                  textAnchor="middle"
                  fontSize={dense ? 10 : 11}
                  fill="var(--ink-3)"
                >
                  {label(p.period)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

export function Stats({
  onOpenAgeBand,
  onOpenPaidIn,
}: {
  onOpenAgeBand: (bandId: string, paidOnly: boolean) => void
  onOpenPaidIn: (period: string) => void
}) {
  const trpc = useTRPC()
  /* Everybody, or only the people who have paid — through the Wix store or an
     optometrist. The same question asked of a different population, so it is a
     filter on this chart rather than a second chart. */
  const [paidOnly, setPaidOnly] = useState(false)
  /* One subject at a time: the age bands and the revenue over time answer
     different questions, and stacking them made the page a scroll. */
  const [subject, setSubject] = useState<'revenue' | 'age'>('revenue')
  /* Dollars or payers, over the same bars. Two sheets of one chart rather than
     two charts: the question "did more people buy, or did the same people pay
     more?" is only answerable by flipping between them in place. */
  const [measure, setMeasure] = useState<Measure>('money')
  const q = useQuery(trpc.analytics.ages.queryOptions({ paidOnly }))
  const revenue = useQuery(trpc.analytics.revenue.queryOptions())
  const { node: tipNode, bind } = useTooltip()
  /* A bar opens the Patients list filtered to it, the same way a bucket does —
     so the people behind a figure arrive with search, sorting, paging and the
     CSV export already built, instead of a second table under the chart that
     has none of them. */
  const toggle = (id: string) => onOpenAgeBand(id, paidOnly)
  const band: string | null = null

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="the age spread" />

  const a = q.data
  const coverage = a.total === 0 ? 0 : Math.round((a.known / a.total) * 100)
  const plotW = W - ML - MR
  const plotH = H - MT - MB
  const slot = plotW / a.bands.length
  const barW = Math.max(6, slot - GAP)
  const top = TOPS.find((t) => t >= Math.max(1, ...a.bands.map((b) => b.count))) ?? 1000
  const y = (v: number) => MT + plotH * (1 - v / top)
  const cx = (i: number) => ML + slot * i + slot / 2

  return (
    <>
      <div className="seg seg-inline" role="tablist" aria-label="Which figures">
        <button
          type="button"
          role="tab"
          aria-selected={subject === 'revenue'}
          onClick={() => setSubject('revenue')}
        >
          Revenue
        </button>
        <button type="button" role="tab" aria-selected={subject === 'age'} onClick={() => setSubject('age')}>
          Age
        </button>
      </div>

      <ViewTitle suit="hand">
        {subject === 'revenue' ? 'What we have been paid' : 'How old everybody is'}
      </ViewTitle>
      {subject === 'age' && (
        <>
      <p className="view-sub">
        Every patient on the sheet, by age today, in five-year bands across the presbyopia window.
        Age is recomputed on read from date of birth, so nobody sits in a band they have aged out
        of — and anyone without a date of birth is counted as unknown rather than guessed into a bar.
      </p>

      <div className="seg seg-inline" role="tablist" aria-label="Which patients">
        <button type="button" role="tab" aria-selected={!paidOnly} onClick={() => setPaidOnly(false)}>
          All patients
        </button>
        <button type="button" role="tab" aria-selected={paidOnly} onClick={() => setPaidOnly(true)}>
          Paid only
        </button>
      </div>

      <div className="tiles">
        <Tile
          k={paidOnly ? 'Paying patients' : 'Patients counted'}
          v={a.total.toLocaleString()}
          d={paidOnly ? 'Wix store and optometrists' : 'test accounts excluded'}
        />
        <Tile
          k="Age on file"
          v={a.known.toLocaleString()}
          d={`${coverage}% of patients — the rest have no date of birth`}
        />
        <Tile k="Median age" v={a.medianAge == null ? '—' : Math.round(a.medianAge)} d="of those on file" />
      </div>

      <Panel
        title="Patients by age band"
        note={a.known === 0 ? undefined : `${a.known.toLocaleString()} with a date of birth`}
      >
        {a.known === 0 ? (
          <p className="footnote">
            Nobody on the sheet has a date of birth yet, so there is nothing to band. The chart
            appears as soon as one arrives.
          </p>
        ) : (
          <div className="chart-wrap">
            <svg
              className="chart"
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={`Patients by age band. ${a.bands
                .map((b) => `${b.label}: ${b.count}`)
                .join('. ')}. Age unknown: ${a.unknown}.`}
            >
              {[0, top / 2, top].map((t) => (
                <g key={t}>
                  <line
                    x1={ML}
                    x2={W - MR}
                    y1={y(t)}
                    y2={y(t)}
                    stroke={t === 0 ? 'var(--rule)' : 'var(--grid)'}
                    strokeWidth={1}
                  />
                  <text x={ML - 10} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--ink-3)">
                    {t}
                  </text>
                </g>
              ))}

              {a.bands.map((b, i) => (
                <g
                  key={b.id}
                  role="button"
                  tabIndex={0}
                  className={`bar-hit${band === b.id ? ' on' : ''}`}
                  aria-label={`${b.label}: ${b.count} patients. Open them in the patients list.`}
                  onClick={() => toggle(b.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggle(b.id)
                    }
                  }}
                  {...bind(
                    <>
                      <b>{b.label}</b>
                      <div className="r">
                        <span>Patients</span>
                        <span>{b.count}</span>
                      </div>
                      <div className="r">
                        <span>Share of known ages</span>
                        <span>{b.pct}%</span>
                      </div>
                    </>,
                  )}
                >
                  {/* A full-height target, so the thin bars of an empty band are
                      still hoverable and the tooltip does not flicker between them. */}
                  <rect x={ML + slot * i} y={MT} width={slot} height={plotH} fill="transparent" />
                  {b.count > 0 && (
                    <rect
                      x={cx(i) - barW / 2}
                      y={y(b.count)}
                      width={barW}
                      height={Math.max(2, y(0) - y(b.count))}
                      rx={4}
                      fill="var(--s1)"
                      opacity={band == null || band === b.id ? 1 : 0.35}
                    />
                  )}
                  <text
                    x={cx(i)}
                    y={y(b.count) - 9}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight={600}
                    fill={b.count === 0 ? 'var(--ink-3)' : 'var(--ink-2)'}
                  >
                    {b.count}
                  </text>
                  <text x={cx(i)} y={H - MB + 22} textAnchor="middle" fontSize={11.5} fill="var(--ink-3)">
                    {b.label}
                  </text>
                </g>
              ))}

              <text x={ML + plotW / 2} y={H - 4} textAnchor="middle" fontSize={11} fill="var(--ink-3)">
                Age today
              </text>
            </svg>
          </div>
        )}

        <table className="stat-table">
          <caption className="sr-only">Patients by age band</caption>
          <thead>
            <tr>
              <th scope="col">Age band</th>
              <th scope="col" className="r">Patients</th>
              <th scope="col" className="r">Share</th>
            </tr>
          </thead>
          <tbody>
            {a.bands.map((b) => (
              <tr
                key={b.id}
                className={`pickable${band === b.id ? ' on' : ''}`}
                tabIndex={0}
                onClick={() => toggle(b.id)}
                onKeyDown={(e) => e.key === 'Enter' && toggle(b.id)}
              >
                <th scope="row">{b.label}</th>
                <td className="r num">{b.count}</td>
                <td className="r num">{b.pct}%</td>
              </tr>
            ))}
            <tr
              className={`unknown-row pickable${band === 'unknown' ? ' on' : ''}`}
              tabIndex={0}
              onClick={() => toggle('unknown')}
              onKeyDown={(e) => e.key === 'Enter' && toggle('unknown')}
            >
              <th scope="row">Age unknown</th>
              <td className="r num">{a.unknown}</td>
              <td className="r num">—</td>
            </tr>
          </tbody>
        </table>

        <p className="footnote">
          {a.unknown > 0 ? (
            <>
              {a.unknown.toLocaleString()} of {a.total.toLocaleString()} patients have no date of
              birth, so the bands describe the {a.known.toLocaleString()} who do and the percentages
              are shares of that number, not of everybody. Oryx records a date of birth only where
              somebody entered one, and the replica currently holds few of them — the bands will
              firm up as coverage does, without anything here changing.
            </>
          ) : (
            <>Every patient on the sheet has a date of birth, so the bands cover everybody.</>
          )}
        </p>
      </Panel>
        </>
      )}

      {subject === 'revenue' && revenue.data && (
        <>
          <p className="view-sub">
            What we have been paid, in dollars — an order where there is one, otherwise the
            practice&rsquo;s cut, each converted at the rate on the day it was taken. Half of this
            arrives in francs, so dollars is the only unit the two can be added in. Israeli VAT is
            excluded: 18% of a shekel payment is collected for the tax authority and was never
            ours, so the patient&rsquo;s record shows what they paid and this shows what we keep.
          </p>

          {/* The same bars in a different unit, for both charts below. */}
          <div className="seg seg-inline" role="tablist" aria-label="Which measure">
            <button type="button" role="tab" aria-selected={measure === 'money'} onClick={() => setMeasure('money')}>
              Revenue
            </button>
            <button type="button" role="tab" aria-selected={measure === 'people'} onClick={() => setMeasure('people')}>
              Paying patients
            </button>
          </div>

          {/* All time first, then the last twelve months: each block is one
              period, its total beside its split, then its chart — three
              numbers from the same period, so they add up. */}
          <h3 className="section-h">All time</h3>
          <div className="tiles">
            <Tile
              k="Revenue, all time"
              v={usdTotal(revenue.data.years)}
              d={`${sum(revenue.data.years, 'people')} paying patients`}
            />
            <Tile
              k="Through optometrists"
              v={usdTotal(revenue.data.years, 'optometristUsdCents')}
              d={`${sum(revenue.data.years, 'optometristPeople')} patients`}
            />
            <Tile
              k="Direct"
              v={usdTotal(revenue.data.years, 'directUsdCents')}
              d={`${sum(revenue.data.years, 'directPeople')} patients`}
            />
          </div>
          <Panel
            title={measure === 'money' ? 'Revenue by year' : 'Paying patients by year'}
            note={`${revenue.data.years.length} years`}
          >
            <RevenueChart points={revenue.data.years} label={(p) => p} measure={measure} bind={bind} onOpen={onOpenPaidIn} />
          </Panel>

          <h3 className="section-h">Last 12 months</h3>
          <div className="tiles">
            <Tile
              k="Revenue, last 12 months"
              v={usdTotal(revenue.data.months)}
              d={`${sum(revenue.data.months, 'people')} paying patients`}
            />
            <Tile
              k="Through optometrists"
              v={usdTotal(revenue.data.months, 'optometristUsdCents')}
              d={`${sum(revenue.data.months, 'optometristPeople')} patients`}
            />
            <Tile
              k="Direct"
              v={usdTotal(revenue.data.months, 'directUsdCents')}
              d={`${sum(revenue.data.months, 'directPeople')} patients`}
            />
          </div>
          <Panel
            title={measure === 'money' ? 'Revenue by month' : 'Paying patients by month'}
            note={
              measure === 'money'
                ? `last 12 months · ${usdTotal(revenue.data.months)}`
                : `last 12 months · ${sum(revenue.data.months, 'people')} patients`
            }
          >
            <RevenueChart
              points={revenue.data.months}
              label={(p) => monthLabel(p)}
              measure={measure}
              bind={bind}
              onOpen={onOpenPaidIn}
            />
            <p className="footnote">
              {measure === 'money'
                ? 'In dollars, each payment converted at the rate on the day it was taken — half of this arrives in francs, and a total in mixed currencies would be meaningless. '
                : 'One per patient, counted in the month they paid rather than the month they started training. '}
              A month nobody paid is drawn as an empty slot rather than left out, so the gaps are
              visible. The orange is what a practice sold; everything else is somebody who bought
              from us. Click a bar to see the patients behind it.
            </p>
          </Panel>

          <h3 className="section-h">Last 30 days</h3>
          <div className="tiles">
            <Tile
              k="Revenue, last 30 days"
              v={usdTotal(revenue.data.days)}
              d={`${sum(revenue.data.days, 'people')} paying patients`}
            />
            <Tile
              k="Through optometrists"
              v={usdTotal(revenue.data.days, 'optometristUsdCents')}
              d={`${sum(revenue.data.days, 'optometristPeople')} patients`}
            />
            <Tile
              k="Direct"
              v={usdTotal(revenue.data.days, 'directUsdCents')}
              d={`${sum(revenue.data.days, 'directPeople')} patients`}
            />
          </div>
          <Panel
            title={measure === 'money' ? 'Revenue by day' : 'Paying patients by day'}
            note={
              measure === 'money'
                ? `last 30 days · ${usdTotal(revenue.data.days)}`
                : `last 30 days · ${sum(revenue.data.days, 'people')} patients`
            }
          >
            <RevenueChart
              points={revenue.data.days}
              label={(p) => dayLabel(p)}
              measure={measure}
              bind={bind}
              onOpen={onOpenPaidIn}
            />
            <p className="footnote">
              One bar per day, today on the right; days run midnight to midnight UTC. A day nobody
              paid is an empty slot. Click a bar to see who paid that day.
            </p>
          </Panel>
        </>
      )}
      {tipNode}
    </>
  )
}

/** "23 Oct" — a day, short enough that fourteen fit across the axis. */
function dayLabel(period: string): string {
  return new Date(`${period}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** "Sep 26" — short enough that twelve of them fit across the axis. */
function monthLabel(period: string): string {
  const [year, month] = period.split('-')
  const name = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-GB', {
    month: 'short',
  })
  return `${name} ${year!.slice(2)}`
}

/** Adds one field across the points — a total, a channel's share, a headcount. */
function sum<K extends keyof RevenuePoint>(points: RevenuePoint[], key: K): number {
  return points.reduce((n, p) => n + (p[key] as number), 0)
}

function usdTotal(
  points: RevenuePoint[],
  key: 'usdCents' | 'optometristUsdCents' | 'directUsdCents' = 'usdCents',
): string {
  const total = sum(points, key)
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(total / 100)
}
